import { getStoredAuthToken } from './api';
import { readWorkstationBinding } from './workstationIdentity';
const LEGACY_KEY = 'maythai_customer_session';
const SESSION_PREFIX = 'maythai_customer_session_v2:';
let deviceCustomer = null;

export function getCustomerSessionAccountId() {
  try {
    const account = JSON.parse(localStorage.getItem('kioskAccount') || 'null');
    const id = Number(account?.parentAccountId || account?.id);
    return Number.isSafeInteger(id) && id > 0 ? String(id) : '';
  } catch { return ''; }
}

// Comparison only, retained in memory; never persist this operator context.
export function getCustomerOperatorScope() {
  return JSON.stringify([getStoredAuthToken(), readWorkstationBinding()]);
}

export function clearCustomerSession(accountId = getCustomerSessionAccountId()) {
  deviceCustomer = null;
  try {
    localStorage.removeItem(LEGACY_KEY);
    if (accountId) localStorage.removeItem(`${SESSION_PREFIX}${accountId}`);
  } catch { /* A blocked storage must not keep an in-memory identity active. */ }
}

function belongsToAccount(customer, accountId) {
  return !!accountId && Number.isSafeInteger(Number(customer?.id)) && Number(customer.id) > 0
    && (customer.accountId == null || String(customer.accountId) === accountId);
}

export function readCustomerSession(accountId = getCustomerSessionAccountId()) {
  try {
    // The historical key has no provable tenant: never migrate its contents.
    localStorage.removeItem(LEGACY_KEY);
    if (!accountId || accountId !== getCustomerSessionAccountId()) return null;
    if (deviceCustomer) {
      if (deviceCustomer.accountId === accountId && deviceCustomer.operatorScope === getCustomerOperatorScope()) return deviceCustomer.customer;
      deviceCustomer = null;
    }
    const entry = JSON.parse(localStorage.getItem(`${SESSION_PREFIX}${accountId}`) || 'null');
    return entry?.accountId === accountId && belongsToAccount(entry.customer, accountId) ? entry.customer : null;
  } catch { return null; }
}

export function saveCustomerSession(customer, accountId = getCustomerSessionAccountId(), { ephemeral = false } = {}) {
  if (accountId !== getCustomerSessionAccountId() || !belongsToAccount(customer, accountId)) return false;
  // Keep only fields used by the kiosk, excluding profile validation tokens.
  const stored = Object.fromEntries(['id', 'accountId', 'firstName', 'phone', 'points', 'availablePoints', 'totalSpent', 'recentProducts']
    .filter((key) => customer[key] !== undefined).map((key) => [key, customer[key]]));
  try {
    localStorage.removeItem(LEGACY_KEY);
    if (ephemeral || (deviceCustomer?.accountId === accountId && deviceCustomer.customer.id === customer.id)) {
      localStorage.removeItem(`${SESSION_PREFIX}${accountId}`);
      deviceCustomer = { accountId, operatorScope: getCustomerOperatorScope(), customer: stored };
    } else {
      deviceCustomer = null;
      localStorage.setItem(`${SESSION_PREFIX}${accountId}`, JSON.stringify({ accountId, customer: stored }));
    }
    return true;
  } catch { return false; }
}

export async function refreshCustomerSession({ customerId, accountId, fetchCustomer, isCurrent = () => true }) {
  const operatorScope = getCustomerOperatorScope();
  const current = () => operatorScope === getCustomerOperatorScope() && accountId && accountId === getCustomerSessionAccountId() && isCurrent();
  if (!current()) return null;
  if (!customerId) { clearCustomerSession(accountId); return null; }
  try {
    const customer = await fetchCustomer(customerId);
    if (!current()) return null;
    if (Number(customer?.id) !== Number(customerId) || !belongsToAccount(customer, accountId)) {
      clearCustomerSession(accountId);
      return null;
    }
    saveCustomerSession(customer, accountId);
    return customer;
  } catch (error) {
    if (!current()) return null;
    // A refused or missing identity is authoritative. Only transport failures
    // may reuse this same tenant's cached profile while the kiosk is offline.
    if (error?.isHttpError || Number(error?.httpStatus || error?.status) >= 400) {
      clearCustomerSession(accountId);
      return null;
    }
    const cached = readCustomerSession(accountId);
    return Number(cached?.id) === Number(customerId) ? cached : null;
  }
}
